import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { resolveInitialLanguage } from '@/stores/language.store';
import { applyDocumentLocale } from '@/lib/locale-direction';

type Theme = 'light' | 'dark' | 'system';

interface UIState {
  sidebarCollapsed: boolean;
  sidebarOpen: boolean;
  theme: Theme;
  locale: string;
  commandPaletteOpen: boolean;
  quickActionsOpen: boolean;
  /** Panneau droit : nouvelle demande de contenus formation (RH). */
  trainingRequestPanelOpen: boolean;

  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSidebarOpen: (open: boolean) => void;
  setTheme: (theme: Theme) => void;
  setLocale: (locale: string) => void;
  toggleCommandPalette: () => void;
  toggleQuickActions: () => void;
  setTrainingRequestPanelOpen: (open: boolean) => void;
}

/** Applique la classe `dark` sur `<html>` selon le thème (utilisé au chargement, au changement de thème, et pour restaurer après une page « forcée » claire). */
export function applyThemeToDocument(theme: Theme): void {
  const root = document.documentElement;

  /** Le mode Malvoyant impose un thème clair à fort contraste. */
  if (root.classList.contains('a11y-high-contrast')) {
    root.classList.remove('dark');
    root.setAttribute('data-theme', 'light');
    return;
  }

  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  root.classList.toggle('dark', isDark);
  root.setAttribute('data-theme', isDark ? 'dark' : 'light');
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      sidebarOpen: false,
      theme: 'system',
      locale: resolveInitialLanguage(),
      commandPaletteOpen: false,
      quickActionsOpen: false,
      trainingRequestPanelOpen: false,

      toggleSidebar: () =>
        set((s) => {
          // Aligné sur le breakpoint `md` (768px) du shell AppLayout / Tailwind.
          const isMobile = window.matchMedia('(max-width: 767px)').matches;
          if (isMobile) {
            return { sidebarOpen: !s.sidebarOpen };
          }
          return { sidebarCollapsed: !s.sidebarCollapsed };
        }),

      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      setTheme: (theme) => {
        applyThemeToDocument(theme);
        set({ theme });
      },

      setLocale: (locale) => {
        localStorage.setItem('locale', locale);
        applyDocumentLocale(locale);
        set({ locale });
      },

      toggleCommandPalette: () => set((s) => ({ commandPaletteOpen: !s.commandPaletteOpen })),
      toggleQuickActions: () => set((s) => ({ quickActionsOpen: !s.quickActionsOpen })),
      setTrainingRequestPanelOpen: (open) => set({ trainingRequestPanelOpen: open }),
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({
        theme: state.theme,
        locale: state.locale,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.theme) {
          applyThemeToDocument(state.theme);
        }
        if (state?.locale) {
          applyDocumentLocale(state.locale);
        }
      },
    },
  ),
);

// Listen for system theme changes when "system" is selected
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const { theme } = useUIStore.getState();
    if (theme === 'system') {
      applyThemeToDocument('system');
    }
  });
}
